// ============================================================
//  Cosmo's Learning Adventure — V6 Entry Point
//  Phaser 3 + TypeScript + Vite
// ============================================================
import Phaser from 'phaser';
import { BootScene } from './game/scenes/BootScene.js';
import { HomeHubScene } from './game/scenes/HomeHubScene.js';
import { NumberKingdomScene } from './game/worlds/number-kingdom/NumberKingdomScene.js';
import { StoryForestScene } from './game/worlds/story-forest/StoryForestScene.js';
import { WordGardenScene } from './game/worlds/story-forest/WordGardenScene.js';
import { DiscoveryLabScene } from './game/worlds/discovery-lab/DiscoveryLabScene.js';
import { StarBridgeScene } from './game/scenes/StarBridgeScene.js';
import { RewardScene } from './game/scenes/RewardScene.js';
import { applicationLifecycle } from './game/shared/lifecycle/index.js';
import { pwaUpdates } from './pwa/PwaUpdateManager.js';
import { KingdomCrossroadsScene } from './game/worlds/number-kingdom/districts/kingdom-crossroads/KingdomCrossroadsScene.js';
import { AdditionCitadelScene } from './game/worlds/number-kingdom/districts/addition-citadel/AdditionCitadelScene.js';
import { NumberForgeScene } from './game/worlds/number-kingdom/districts/addition-citadel/number-forge/NumberForgeScene.js';
import { RegroupingWorkshopScene } from './game/worlds/number-kingdom/districts/addition-citadel/regrouping-workshop/RegroupingWorkshopScene.js';
import { CharacterGroveScene } from './game/worlds/story-forest/CharacterGroveScene.js';
import { MaterialsWorkshopScene } from './game/worlds/discovery-lab/materials-workshop/MaterialsWorkshopScene.js';
import { SubtractionKeepScene } from './game/worlds/number-kingdom/districts/subtraction-keep/SubtractionKeepScene.js';
import { MeasurementMarketScene } from './game/worlds/number-kingdom/districts/measurement-market/MeasurementMarketScene.js';
import { ClocktowerPlazaScene } from './game/worlds/number-kingdom/districts/clocktower-plaza/ClocktowerPlazaScene.js';
import { CoinmakersVaultScene } from './game/worlds/number-kingdom/districts/coinmakers-vault/CoinmakersVaultScene.js';
import { RoyalChallengeHallScene } from './game/worlds/number-kingdom/districts/royal-challenge-hall/RoyalChallengeHallScene.js';
import { SequenceStreamScene } from './game/worlds/story-forest/sequence-stream/SequenceStreamScene.js';
import { MainIdeaMeadowScene } from './game/worlds/story-forest/main-idea-meadow/MainIdeaMeadowScene.js';
import { InferenceHollowScene } from './game/worlds/story-forest/inference-hollow/InferenceHollowScene.js';
import { AuthorsCabinScene } from './game/worlds/story-forest/authors-cabin/AuthorsCabinScene.js';
import { GrammarGladeScene } from './game/worlds/story-forest/grammar-glade/GrammarGladeScene.js';
import { FluencyLanternScene } from './game/worlds/story-forest/fluency-lantern-path/FluencyLanternScene.js';
import { GreatStoryTreeScene } from './game/worlds/story-forest/great-story-tree/GreatStoryTreeScene.js';
import { WeatherWatchScene } from './game/worlds/discovery-lab/weather-watch-station/WeatherWatchScene.js';
import { HabitatFieldScene } from './game/worlds/discovery-lab/habitat-field-camp/HabitatFieldScene.js';
import { PlantGrowthScene } from './game/worlds/discovery-lab/plant-growth-house/PlantGrowthScene.js';
import { LifeCycleScene } from './game/worlds/discovery-lab/life-cycle-pond/LifeCycleScene.js';
import { SoundVibrationScene } from './game/worlds/discovery-lab/sound-vibration-deck/SoundVibrationScene.js';
import { EarthMaterialsScene } from './game/worlds/discovery-lab/earth-materials-yard/EarthMaterialsScene.js';
import { DiscoveryObservatoryScene } from './game/worlds/discovery-lab/discovery-observatory/DiscoveryObservatoryScene.js';

declare const __BUILD_REVISION__: string;

document.documentElement.dataset.buildRevision = __BUILD_REVISION__;

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  width: 1280,
  height: 720,
  backgroundColor: '#0d0221',
  parent: 'game-container',
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  scene: [
    BootScene,
    HomeHubScene,
    NumberKingdomScene,
    KingdomCrossroadsScene,
    AdditionCitadelScene,
    NumberForgeScene,
    RegroupingWorkshopScene,
    SubtractionKeepScene,
    MeasurementMarketScene,
    ClocktowerPlazaScene,
    CoinmakersVaultScene,
    RoyalChallengeHallScene,
    StoryForestScene,
    WordGardenScene,
    CharacterGroveScene,
    SequenceStreamScene,
    MainIdeaMeadowScene,
    InferenceHollowScene,
    AuthorsCabinScene,
    GrammarGladeScene,
    FluencyLanternScene,
    GreatStoryTreeScene,
    DiscoveryLabScene,
    MaterialsWorkshopScene,
    WeatherWatchScene,
    HabitatFieldScene,
    PlantGrowthScene,
    LifeCycleScene,
    SoundVibrationScene,
    EarthMaterialsScene,
    DiscoveryObservatoryScene,
    StarBridgeScene,
    RewardScene,
  ],
  physics: {
    default: 'arcade',
    arcade: { gravity: { x: 0, y: 0 }, debug: false },
  },
  render: {
    antialias: true,
    antialiasGL: true,
    pixelArt: false,
    roundPixels: false,
    powerPreference: 'high-performance',
  },
};

const game = new Phaser.Game(config);
applicationLifecycle.start(game);
pwaUpdates.register();
export default game;
